'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { cva, type VariantProps } from 'class-variance-authority'
import {
  Popover as AriaPopover,
  type PopoverProps as AriaPopoverProps,
} from 'react-aria-components'
import { PopoverArrow } from './_components/PopoverArrow'

const DEFAULT_OFFSET = 8
const OFFSET_WITH_ARROW = 12

const popoverCva = cva('bg-white', {
  variants: {
    variant: {
      default:
        'z-popover rounded-sm border-px border-solid border-brand-2-900 outline-none data-[entering]:animate-[fade-in_150ms_ease-out] data-[exiting]:animate-[fade-out_100ms_ease-in] hd:border-1.5',
      plain: '',
    },
  },
})

type PopoverVariantProps = VariantProps<typeof popoverCva>

type PopoverVariant = NonNullable<PopoverVariantProps['variant']>

export type PopoverProps = UiComponentProps<
  StrictOmit<AriaPopoverProps, 'className' | 'children'> & {
    variant: PopoverVariant
    /** Whether the arrow pointing at the trigger is displayed */
    showArrow?: boolean
    children: AriaPopoverProps['children']
  }
>

export function Popover({
  children,
  variant,
  showArrow = false,
  className,
  offset,
  'data-testid': dataTestId = 'popover',
  'aria-label': ariaLabel,
  ...props
}: PopoverProps) {
  const shouldShowArrow = variant === 'default' && showArrow

  return (
    <AriaPopover
      {...props}
      offset={offset ?? (shouldShowArrow ? OFFSET_WITH_ARROW : DEFAULT_OFFSET)}
      data-testid={dataTestId}
      aria-label={ariaLabel}
      className={twMerge(className, popoverCva({ variant }), shouldShowArrow && 'overflow-visible')}
    >
      {(renderProps) => (
        <>
          {shouldShowArrow && <PopoverArrow dataTestId={dataTestId} />}
          {typeof children === 'function' ? children(renderProps) : children}
        </>
      )}
    </AriaPopover>
  )
}
